// Geodesy helpers for the map tools. Points are [lat, lng] in degrees (WGS-84).

const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

// WGS-84 ellipsoid
const A = 6378137;
const F = 1 / 298.257223563;
const B = (1 - F) * A;

export const M_PER = { km: 1000, nm: 1852, mi: 1609.344 };
export const UNIT_LABEL = { km: "km", nm: "nmi", mi: "mi" };
export const UNIT_NAME = { km: "Kilometres", nm: "Nautical miles", mi: "Miles" };

// Clicking a repeated copy of the world gives longitudes beyond ±180; bring them back.
export const wrapLng = (lng) => ((((lng + 180) % 360) + 360) % 360) - 180;
export const wrapPoint = ([lat, lng]) => [lat, wrapLng(lng)];

// Great-circle distance on a sphere, in metres. Used as the fallback and for cheap pre-filtering.
export function haversineM([lat1, lng1], [lat2, lng2]) {
  const p1 = rad(lat1), p2 = rad(lat2);
  const a = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Geodesic distance on the WGS-84 ellipsoid (Vincenty's inverse formula), in metres.
// Accurate to well under a millimetre; the old spherical formula was off by up to ~0.5%.
// Falls back to haversine for the rare near-antipodal pairs where Vincenty doesn't converge.
export function distanceM(p1, p2) {
  const [lat1, lng1] = wrapPoint(p1);
  const [lat2, lng2] = wrapPoint(p2);
  if (lat1 === lat2 && lng1 === lng2) return 0;

  const L = rad(lng2 - lng1);
  const U1 = Math.atan((1 - F) * Math.tan(rad(lat1)));
  const U2 = Math.atan((1 - F) * Math.tan(rad(lat2)));
  const sinU1 = Math.sin(U1), cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);

  let lambda = L, prev, iter = 0;
  let sinSigma, cosSigma, sigma, cosSqAlpha, cos2SigmaM;
  do {
    const sinL = Math.sin(lambda), cosL = Math.cos(lambda);
    sinSigma = Math.sqrt((cosU2 * sinL) ** 2 + (cosU1 * sinU2 - sinU1 * cosU2 * cosL) ** 2);
    if (sinSigma === 0) return 0; // coincident points
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosL;
    sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinL) / sinSigma;
    cosSqAlpha = 1 - sinAlpha * sinAlpha;
    cos2SigmaM = cosSqAlpha !== 0 ? cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha : 0; // equatorial line
    const C = (F / 16) * cosSqAlpha * (4 + F * (4 - 3 * cosSqAlpha));
    prev = lambda;
    lambda = L + (1 - C) * F * sinAlpha * (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
  } while (Math.abs(lambda - prev) > 1e-12 && ++iter < 200);
  if (iter >= 200) return haversineM([lat1, lng1], [lat2, lng2]);

  const uSq = (cosSqAlpha * (A * A - B * B)) / (B * B);
  const k1 = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const k2 = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const dSigma =
    k2 * sinSigma * (cos2SigmaM + (k2 / 4) * (cosSigma * (-1 + 2 * cos2SigmaM ** 2) - (k2 / 6) * cos2SigmaM * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaM ** 2)));
  return B * k1 * (sigma - dSigma);
}

// The point reached by travelling `distM` metres from `start` on initial bearing `bearing` (Vincenty's direct
// formula). The longitude is NOT wrapped, so a ring of these points around one centre stays continuous.
export function destination([lat1, lng1], bearing, distM) {
  const a1 = rad(bearing);
  const sinA1 = Math.sin(a1), cosA1 = Math.cos(a1);
  const tanU1 = (1 - F) * Math.tan(rad(lat1));
  const cosU1 = 1 / Math.sqrt(1 + tanU1 * tanU1), sinU1 = tanU1 * cosU1;
  const sigma1 = Math.atan2(tanU1, cosA1);
  const sinAlpha = cosU1 * sinA1;
  const cosSqAlpha = 1 - sinAlpha * sinAlpha;
  const uSq = (cosSqAlpha * (A * A - B * B)) / (B * B);
  const k1 = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const k2 = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));

  let sigma = distM / (B * k1), prev, iter = 0, cos2SigmaM, sinSigma, cosSigma;
  do {
    cos2SigmaM = Math.cos(2 * sigma1 + sigma);
    sinSigma = Math.sin(sigma);
    cosSigma = Math.cos(sigma);
    const dSigma = k2 * sinSigma * (cos2SigmaM + (k2 / 4) * (cosSigma * (-1 + 2 * cos2SigmaM ** 2) - (k2 / 6) * cos2SigmaM * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaM ** 2)));
    prev = sigma;
    sigma = distM / (B * k1) + dSigma;
  } while (Math.abs(sigma - prev) > 1e-12 && ++iter < 100);

  const x = sinU1 * sinSigma - cosU1 * cosSigma * cosA1;
  const lat2 = Math.atan2(sinU1 * cosSigma + cosU1 * sinSigma * cosA1, (1 - F) * Math.sqrt(sinAlpha * sinAlpha + x * x));
  const lambda = Math.atan2(sinSigma * sinA1, cosU1 * cosSigma - sinU1 * sinSigma * cosA1);
  const C = (F / 16) * cosSqAlpha * (4 + F * (4 - 3 * cosSqAlpha));
  const L = lambda - (1 - C) * F * sinAlpha * (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
  return [deg(lat2), lng1 + deg(L)];
}

// A true geodesic circle (every point exactly `radiusM` from the centre) as a closed ring of [lat, lng].
export function circleRing(center, radiusM, steps = 96) {
  const ring = [];
  for (let i = 0; i <= steps; i++) ring.push(destination(center, (360 * i) / steps, radiusM));
  return ring;
}

export function pathDistanceM(points) {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += distanceM(points[i - 1], points[i]);
  return total;
}

// Initial compass bearing from a to b, 0–360° clockwise from north.
export function bearingDeg([lat1, lng1], [lat2, lng2]) {
  const p1 = rad(lat1), p2 = rad(lat2), dl = rad(lng2 - lng1);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
export const compassPoint = (b) => COMPASS[Math.round(b / 22.5) % 16];
export const formatBearing = (b) => `${Math.round(b).toString().padStart(3, "0")}° ${compassPoint(b)}`;

// Points along the great circle from a to b (inclusive), so a long measurement is drawn as the
// curve it really is instead of a straight line on the flat map. Longitudes stay continuous across
// the antimeridian (e.g. 179 → 181) so Leaflet doesn't draw a line the wrong way round the world.
export function greatCircle(a, b, refLng = a[1]) {
  const p1 = rad(a[0]), l1 = rad(a[1]), p2 = rad(b[0]), l2 = rad(b[1]);
  const d = 2 * Math.asin(Math.min(1, Math.sqrt(Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2)));
  if (d < 1e-9) return [[a[0], refLng], [b[0], refLng]];
  const steps = Math.min(180, Math.max(2, Math.ceil(deg(d) * 2))); // a point every ~0.5°
  const out = [];
  let prev = refLng;
  for (let i = 0; i <= steps; i++) {
    const f = i / steps;
    const k1 = Math.sin((1 - f) * d) / Math.sin(d);
    const k2 = Math.sin(f * d) / Math.sin(d);
    const x = k1 * Math.cos(p1) * Math.cos(l1) + k2 * Math.cos(p2) * Math.cos(l2);
    const y = k1 * Math.cos(p1) * Math.sin(l1) + k2 * Math.cos(p2) * Math.sin(l2);
    const z = k1 * Math.sin(p1) + k2 * Math.sin(p2);
    const lat = deg(Math.atan2(z, Math.hypot(x, y)));
    let lng = deg(Math.atan2(y, x));
    while (lng - prev > 180) lng -= 360;
    while (lng - prev < -180) lng += 360;
    out.push([lat, lng]);
    prev = lng;
  }
  return out;
}

// A whole measured route as one continuous list of [lat, lng] points, plus where each segment's middle is.
export function routePath(points) {
  const path = [];
  const mids = [];
  let lng = points[0]?.[1] ?? 0;
  for (let i = 1; i < points.length; i++) {
    const seg = greatCircle(points[i - 1], points[i], lng);
    mids.push(seg[Math.floor(seg.length / 2)]);
    path.push(...(i === 1 ? seg : seg.slice(1)));
    lng = seg[seg.length - 1][1];
  }
  return { path, mids };
}

/* ---------- formatting ---------- */

export function formatDistance(meters, unit = "km") {
  if (unit === "km" && meters < 1000) return `${Math.round(meters)} m`;
  const v = meters / M_PER[unit];
  const digits = v < 10 ? 2 : v < 1000 ? 1 : 0;
  return `${v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${UNIT_LABEL[unit]}`;
}

function dms(value, pos, neg, seconds = 2) {
  const dir = value >= 0 ? pos : neg;
  const abs = Math.abs(value);
  let d = Math.floor(abs);
  let m = Math.floor((abs - d) * 60);
  let s = ((abs - d) * 60 - m) * 60;
  if (Number(s.toFixed(seconds)) >= 60) { s = 0; m += 1; }
  if (m >= 60) { m = 0; d += 1; }
  return `${d}°${String(m).padStart(2, "0")}′${s.toFixed(seconds).padStart(seconds + 3, "0")}″${dir}`;
}

// "41.73250, -49.94690"  or  41°43′57.00″N 49°56′48.84″W   (input is wrapped to ±180 first)
export function formatLatLng(lat, lng, format = "dd") {
  const w = wrapLng(lng);
  return format === "dms" ? `${dms(lat, "N", "S")} ${dms(w, "E", "W")}` : `${lat.toFixed(5)}, ${w.toFixed(5)}`;
}
